import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';
import { toast } from 'sonner';
import {
  User, Phone, Globe, MessageSquare, Check, Loader2, ArrowRight, Sparkles, UserCheck, Scale,
} from 'lucide-react';
import { LANGUAGES, defaultSpokenLanguageForUiLocale } from '../mock';
import FormLegalNotice from '../components/FormLegalNotice';
import { useLanguage } from '../i18n/LanguageContext';
import {
  buildCampaignCapturePayload, getJourneyId, trackEvent, trackPageLifecycle,
} from '../lib/customerEventTracker';
import { appendLeadToGoogleSheets, isGoogleSheetsConfigured } from '../lib/googleSheetsLead';
import { goToPostSubmit } from '../lib/postSubmitGuest';
import ServiceContent from '../components/ServiceContent';
import { SERVICE_CONTENT } from '../data/serviceContent';
import TrustStrip from '../components/TrustStrip';

const PLANS = [
  {
    code: 'CA_CONSULTATION',
    icon: UserCheck,
    title: 'CA Consultation',
    price: '₹1,988',
    priceNote: 'All Inclusive',
    features: ['Online consultation', '30 minute session', 'Preferred language selection', 'Tax & compliance guidance'],
  },
  {
    code: 'LEGAL_CONSULTATION',
    icon: Scale,
    title: 'Legal Consultation',
    price: '₹1,988',
    priceNote: 'All Inclusive',
    features: ['Online consultation', '30 minute session', 'Preferred language selection', 'Legal & documentation guidance'],
  },
];

const INITIAL_FORM = { full_name: '', mobile: '', language: '', message: '' };

const Consultation = () => {
  const { t, locale } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselected = searchParams.get('service') === 'LEGAL_CONSULTATION' ? 'LEGAL_CONSULTATION' : 'CA_CONSULTATION';

  const [planCode, setPlanCode] = useState(preselected);
  const [form, setForm] = useState(() => ({
    ...INITIAL_FORM,
    language: defaultSpokenLanguageForUiLocale(locale),
  }));
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const selectedPlan = PLANS.find((p) => p.code === planCode) || PLANS[0];

  useEffect(
    () => trackPageLifecycle({
      funnel_name: 'CONSULTATION_FORM',
      form_name: 'ConsultationForm',
      service_code: planCode,
      open_event_name: 'consultation_form_open',
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const setField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => (prev[key] ? { ...prev, [key]: undefined } : prev));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const nextErrors = {};
    if (!form.full_name.trim()) nextErrors.full_name = t('form.err_name_simple', 'Name is required');
    if (!/^[0-9]{10}$/.test(form.mobile)) nextErrors.mobile = t('form.err_mobile', 'Enter a valid 10-digit mobile number');
    if (!form.language) nextErrors.language = t('sf.err_language', 'Select a language');
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      trackEvent({
        event_name: 'consultation_submit_failed',
        event_action: 'submit',
        event_status: 'failed',
        event_category: 'form',
        form_name: 'ConsultationForm',
        funnel_name: 'CONSULTATION_FORM',
        funnel_step_number: 2,
        funnel_step_name: 'submit',
        journey_id: getJourneyId('CONSULTATION_FORM'),
        service_code: planCode,
        error_code: 'validation_error',
        error_message: 'Client-side validation failed',
      });
      toast.error(t('form.err_fix_toast', 'Please fix the highlighted errors'));
      return;
    }

    trackEvent({
      event_name: 'consultation_submit_attempt',
      event_action: 'submit',
      event_status: 'attempt',
      event_category: 'form',
      form_name: 'ConsultationForm',
      funnel_name: 'CONSULTATION_FORM',
      funnel_step_number: 2,
      funnel_step_name: 'submit',
      journey_id: getJourneyId('CONSULTATION_FORM'),
      service_code: planCode,
      phone_number: form.mobile,
    });

    const capture = buildCampaignCapturePayload();
    const payload = {
      full_name: form.full_name.trim(),
      mobile: form.mobile.trim(),
      language: form.language,
      remark: form.message.trim() || null,
      service_required: [planCode],
      tag: 'client_website',
      ...capture,
      lead_source: capture.lead_source || 'WEBSITE',
    };

    setSubmitting(true);
    try {
      if (!isGoogleSheetsConfigured()) {
        trackEvent({
          event_name: 'consultation_submit_failed',
          event_action: 'submit',
          event_status: 'failed',
          event_category: 'form',
          form_name: 'ConsultationForm',
          funnel_name: 'CONSULTATION_FORM',
          funnel_step_number: 2,
          funnel_step_name: 'submit',
          journey_id: getJourneyId('CONSULTATION_FORM'),
          service_code: planCode,
          phone_number: form.mobile,
          error_code: 'missing_sheets_env',
          error_message: 'REACT_APP_GOOGLE_SHEETS_WEBAPP_URL not set',
        });
        toast.error(
          t(
            'form.err_sheets_missing',
            'Add REACT_APP_GOOGLE_SHEETS_WEBAPP_URL in frontend/.env (Apps Script URL ending in /exec), then restart the dev server.',
          ),
        );
        return;
      }
      appendLeadToGoogleSheets({
        form: 'consultation',
        summary: `${payload.full_name} | ${payload.mobile} | ${selectedPlan.title}`,
        detail: payload,
      });
      trackEvent({
        event_name: 'consultation_submit_success',
        event_action: 'submit',
        event_status: 'success',
        event_category: 'form',
        form_name: 'ConsultationForm',
        funnel_name: 'CONSULTATION_FORM',
        funnel_step_number: 2,
        funnel_step_name: 'submit',
        journey_id: getJourneyId('CONSULTATION_FORM', { reset: true }),
        service_code: planCode,
        phone_number: form.mobile,
        api_name: 'google_sheets',
        api_status_code: null,
      });
      toast.success(t('form.toast_submit_ok_public', 'Submitted successfully. Our team will contact you soon.'));
      goToPostSubmit(navigate, form.full_name);
    } catch (err) {
      trackEvent({
        event_name: 'consultation_submit_failed',
        event_action: 'submit',
        event_status: 'failed',
        event_category: 'form',
        form_name: 'ConsultationForm',
        funnel_name: 'CONSULTATION_FORM',
        funnel_step_number: 2,
        funnel_step_name: 'submit',
        journey_id: getJourneyId('CONSULTATION_FORM'),
        service_code: planCode,
        phone_number: form.mobile,
        error_code: 'unexpected',
        error_message: String(err?.message || err),
      });
      toast.error(t('contact.toast_backend', 'Backend: {msg}', { msg: err?.message || 'Unexpected error' }));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="pt-28 pb-16 hero-gradient min-h-screen">
      <div className="max-w-5xl mx-auto px-4 md:px-8">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-[#14A098]/20 mb-4 shadow-sm">
            <Sparkles className="w-4 h-4 text-[#14A098]" />
            <span className="text-xs font-semibold text-[#0f2540]">{t('consult.badge', 'Talk to a verified expert')}</span>
          </div>
          <h1 className="ui-title-xl mb-4">{t('consult.title', 'Online Expert Consultation')}</h1>
          <p className="ui-body max-w-2xl mx-auto mb-6">
            {t(
              'consult.subtitle',
              'Get expert guidance from a verified Chartered Accountant or legal expert with a 30-minute consultation for tax filing, GST, company registration, business compliance and legal matters.',
            )}
          </p>
          <TrustStrip />
        </div>

        <div className="bg-white rounded-3xl shadow-xl border border-gray-100 p-6 md:p-10">
          <h2 className="text-xl font-bold text-[#0f2540] mb-5">{t('consult.choose_type', 'Choose Consultation Type')}</h2>
          <div className="grid sm:grid-cols-2 gap-4 mb-8">
            {PLANS.map((plan) => {
              const active = planCode === plan.code;
              return (
                <button
                  key={plan.code}
                  type="button"
                  onClick={() => setPlanCode(plan.code)}
                  className={`text-left p-5 rounded-2xl border-2 transition-colors ${
                    active ? 'border-[#14A098] bg-teal-50/50' : 'border-gray-100 hover:border-[#14A098]/40'
                  }`}
                >
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="flex items-center gap-2.5">
                      <span
                        className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 ${
                          active ? 'border-[#14A098]' : 'border-gray-300'
                        }`}
                        aria-hidden
                      >
                        {active && <span className="w-2.5 h-2.5 rounded-full bg-[#14A098]" />}
                      </span>
                      <plan.icon className="w-4 h-4 text-[#14A098] shrink-0" />
                      <span className="font-bold text-[#0f2540]">{t(`consult.plan_${plan.code}_title`, plan.title)}</span>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="font-extrabold text-[#0f2540]">{plan.price}</div>
                      <div className="text-[11px] text-gray-500">{t('consult.all_inclusive', plan.priceNote)}</div>
                    </div>
                  </div>
                  <ul className="space-y-1.5 pl-7">
                    {plan.features.map((f, idx) => (
                      <li key={f} className="flex items-center gap-2 text-sm text-gray-600">
                        <Check className="w-3.5 h-3.5 text-[#14A098] shrink-0" />
                        {t(`consult.plan_${plan.code}_feature_${idx}`, f)}
                      </li>
                    ))}
                  </ul>
                </button>
              );
            })}
          </div>

          <form onSubmit={onSubmit} className="space-y-5">
            <div className="grid md:grid-cols-2 gap-5">
              <div className="space-y-2">
                <Label className="text-sm font-medium text-[#0f2540] flex items-center gap-1.5">
                  <User className="w-3.5 h-3.5 text-[#14A098]" /> {t('itr.full_name', 'Full Name')} <span className="text-rose-500">*</span>
                </Label>
                <Input
                  value={form.full_name}
                  onChange={(e) => setField('full_name', e.target.value)}
                  placeholder={t('itr.placeholder_name', 'e.g. Rahul Sharma')}
                  className="h-11"
                />
                {errors.full_name && <p className="text-xs text-rose-500">{errors.full_name}</p>}
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium text-[#0f2540] flex items-center gap-1.5">
                  <Phone className="w-3.5 h-3.5 text-[#14A098]" /> {t('itr.mobile', 'Mobile Number')} <span className="text-rose-500">*</span>
                </Label>
                <Input
                  value={form.mobile}
                  onChange={(e) => setField('mobile', e.target.value.replace(/\D/g, '').slice(0, 10))}
                  inputMode="numeric"
                  placeholder={t('itr.placeholder_mobile', '10-digit mobile')}
                  className="h-11"
                />
                {errors.mobile && <p className="text-xs text-rose-500">{errors.mobile}</p>}
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium text-[#0f2540] flex items-center gap-1.5">
                <Globe className="w-3.5 h-3.5 text-[#14A098]" /> {t('itr.pref_lang', 'Preferred Language')} <span className="text-rose-500">*</span>
              </Label>
              <Select value={form.language} onValueChange={(v) => setField('language', v)}>
                <SelectTrigger className="h-11"><SelectValue placeholder={t('itr.placeholder_lang', 'Select language')} /></SelectTrigger>
                <SelectContent>{LANGUAGES.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
              </Select>
              {errors.language && <p className="text-xs text-rose-500">{errors.language}</p>}
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium text-[#0f2540] flex items-center gap-1.5">
                <MessageSquare className="w-3.5 h-3.5 text-[#14A098]" /> {t('consult.how_help', 'How can we help?')}
              </Label>
              <Textarea
                value={form.message}
                onChange={(e) => setField('message', e.target.value)}
                rows={4}
                placeholder={t('consult.how_help_ph', 'Briefly describe what you need help with...')}
              />
            </div>

            <div className="flex flex-wrap items-center gap-3 pt-1">
              <Button
                type="submit"
                size="lg"
                disabled={submitting}
                className="bg-[#14A098] hover:bg-[#0e7a74] text-white rounded-full px-8 h-12 min-w-[200px] whitespace-normal text-center leading-snug"
              >
                {submitting
                  ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t('itr.submitting', 'Submitting...')}</>)
                  : (<>{t('consult.book_now', 'Book Consultation')} <ArrowRight className="w-4 h-4 ml-2" /></>)}
              </Button>
              <FormLegalNotice className="text-xs text-gray-500 w-full sm:w-auto sm:flex-1" />
            </div>
          </form>
        </div>

        {SERVICE_CONTENT[planCode] && (
          <ServiceContent
            code={planCode}
            title={t(`consult.plan_${planCode}_title`, selectedPlan.title)}
            content={SERVICE_CONTENT[planCode]}
          />
        )}
      </div>
    </div>
  );
};

export default Consultation;
